import {
  Box,
  Button,
  Card,
  FormikSelectInputNative,
  FormikTextareaInput,
  FormikTextInput,
  Icon,
  Modal,
  TextLink,
  toast,
  Toggle,
} from '@palmetto/palmetto-components';
import { Field, Formik } from 'formik';
import { ChangeEvent, useMemo, useState } from 'react';
import { Form } from 'react-router-dom';
import * as yup from 'yup';

import {
  FlagCriteriaFilterType,
  FlagDisplayTextUpdate,
  RequirementAcceptanceCriterion,
  RequirementAcceptanceProblem,
  RequirementFlagSettingsDocument,
  RequirementFlagSettingsItemType,
  RequirementFlagSettingsStatus,
  RequirementFlagSettingsType,
  RequirementFlagSettingPatchRequest,
} from 'types';

import { getStateOptions } from '../../helpers/getStateOptions';
import { isErrorWithData } from '../../services/helpers';
import { useUpdateRequirementFlagSettingMutation } from '../../services/requirementFlagSettings';

const flagSettingSchema = yup.object().shape({});

const generateNewProblemFields = ({ count }: { count: number }) => {
  const newFields = [];
  for (let i = 0; i < count; i++) {
    const problemKey = `newProblem-${i}`;
    newFields.push(
      <Card childGap="sm" key={problemKey}>
        <Card.Header>
          <Field
            type="text"
            label={`New Problem ${i + 1}`}
            name={problemKey}
            id={problemKey}
            component={FormikTextareaInput}
          />
        </Card.Header>
      </Card>,
    );
  }
  return newFields;
};

const NewCriterionForm = ({ name }: { name: string }) => (
  <Card overflow="scroll">
    <Card.Header childGap="sm">
      <Box childGap="sm" fontSize="lg" fontWeight="bold" padding="0 0 lg 0">
        {`New ${name} Requirement`}
      </Box>
      <Field type="text" label="Display Text" name="displayText" id="display" component={FormikTextareaInput} />
      <Field type="text" label="Description" name="description" id="description" component={FormikTextareaInput} />
      <Field
        type="text"
        label="Knowledge Base Article"
        name="knowledgeBaseArticle"
        id="knowledgeBaseArticle"
        component={FormikTextInput}
      />
    </Card.Header>
    <Card.Section childGap="sm">
      <Box borderWidth="0 0 xs 0" borderColor="grey-100" padding="0 0 sm 0" fontWeight="bold">
        New Issues
      </Box>
      {generateNewProblemFields({ count: 5 })}
    </Card.Section>
  </Card>
);

const NewProblemForm = ({ name }: { name: string; parentId: string }) => (
  <Card>
    <Card.Header childGap="sm">
      <Box childGap="sm" fontSize="lg" fontWeight="bold" padding="0 0 lg 0">
        {`New ${name} Issue`}
      </Box>
      <Field type="text" label="Display Text" name="displayText" id="display" component={FormikTextareaInput} />
      <Field
        type="text"
        label="Issue Details (optional; comma separated)"
        name="details"
        id="details"
        component={FormikTextareaInput}
      />
    </Card.Header>
  </Card>
);

export const EditFlagCriteriaModal = ({
  isOpen,
  handleClose,
  flagSetting,
}: {
  isOpen: boolean;
  handleClose: () => void;
  flagSetting: RequirementFlagSettingsDocument & { id: string };
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [addNewCriterion, setAddNewCriterion] = useState(false);
  const [addNewProblemForCriterion, setNewProblemForCriterion] = useState('');
  const [removingItem, setRemovingItem] = useState('');
  const criteriaProblems: Record<string, Record<string, string | boolean>[]> = {};
  const criterionProps: {
    criterion: string;
    description: string;
    knowledgeBaseArticle: string;
    stateFilter: string;
    canBeRemoved?: boolean;
  }[] = [];

  const editableSettings = flagSetting?.acceptanceCriteria?.reduce(
    (acc: Record<string, string | undefined>, criterion) => {
      const criterionProp = `criterion-${criterion.id.toString()}`;
      const criterionDescriptionProp = `description-${criterion.id.toString()}`;
      const criterionKnowledgeBaseArticleProp = `knowledgeBaseArticle-${criterion.id.toString()}`;
      const criterionStateFiltersProp = `stateFilter-${criterion.id.toString()}`;
      criterionProps.push({
        criterion: criterionProp,
        description: criterionDescriptionProp,
        knowledgeBaseArticle: criterionKnowledgeBaseArticleProp,
        stateFilter: criterionStateFiltersProp,
        canBeRemoved: criterion.canBeRemoved ?? true,
      });
      const problems = [];
      acc[criterionProp] = criterion.displayText;
      acc[criterionDescriptionProp] = criterion.description;
      acc[criterionKnowledgeBaseArticleProp] = criterion.knowledgeBaseArticle;
      acc[criterionStateFiltersProp] = criterion.filters?.find(
        (filter) => filter.type === FlagCriteriaFilterType.addressState,
      )?.value;

      for (const problem of criterion.problems) {
        const problemProp = `problem-${problem.id.toString()}-${criterionProp}`;
        const problemDetailsProp = `details-${problem.id.toString()}-${criterionProp}`;
        acc[problemProp] = problem.displayText;
        if (problem?.details?.length) {
          acc[problemDetailsProp] = problem?.details?.join(', ');
        }
        problems.push({ [problemProp]: problem.displayText, canBeRemoved: problem.canBeRemoved ?? true });
      }
      if (problems?.length > 0) {
        criteriaProblems[criterionProp] = problems;
      }
      return acc;
    },
    {},
  );

  const totalCriteriaCount = criterionProps.length;

  const handleDisplayAddCriterion = () => {
    setAddNewCriterion(true);
  };

  const handleDisplayAddProblem = (parentKey: string) => {
    setNewProblemForCriterion(parentKey);
  };

  const handleRemoveItem = (itemKey: string) => {
    setRemovingItem(itemKey);
  };

  const initialValues: any = useMemo(
    () => ({
      name: flagSetting?.name,
      ...editableSettings,
      status: flagSetting?.status,
      isFilterable: flagSetting?.isFilterable,
    }),
    [editableSettings, flagSetting?.name, flagSetting?.status],
  );

  const [updateFlagSetting] = useUpdateRequirementFlagSettingMutation();

  const handleUpdate = async (updates: RequirementFlagSettingPatchRequest) => {
    try {
      await updateFlagSetting({
        id: flagSetting?.id,
        body: updates,
      }).unwrap();
      toast.success('Flag setting updated');
      setIsEditing(false);
      handleClose();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        console.error(e);
        toast.error('Error updating flag Setting');
      }
    }
  };

  const handleSubmit = async (values: any) => {
    let updates = {} as RequirementFlagSettingPatchRequest;
    const knowledgeBaseArticleErrors: string[] = [];

    // If changing status, then send it.
    if (initialValues.status !== values.status) {
      updates.status = values.status;
    }

    // Only send display text diffs when status is not being set or is being set to active from inactive.
    if (
      !updates.status ||
      updates.status === RequirementFlagSettingsStatus.active ||
      !addNewCriterion ||
      !addNewProblemForCriterion
    ) {
      const acceptanceCriteria: FlagDisplayTextUpdate[] = [];
      const problems: FlagDisplayTextUpdate[] = [];

      Object.keys(values).forEach((key: string) => {
        const criterionId = key.split('-')[1];
        if (key.startsWith('knowledgeBaseArticle-')) {
          if (!yup.string().url().isValidSync(values[`knowledgeBaseArticle-${criterionId}`])) {
            knowledgeBaseArticleErrors.push(values[`criterion-${criterionId}`]);
          }
        }
        if (values[key] !== initialValues[key]) {
          if (
            key.startsWith('criterion-') ||
            key.startsWith('description-') ||
            key.startsWith('knowledgeBaseArticle-') ||
            key.startsWith('stateFilter-')
          ) {
            const displayText = values[`criterion-${criterionId}`];
            const description = values[`description-${criterionId}`];
            const knowledgeBaseArticle = values[`knowledgeBaseArticle-${criterionId}`];
            const stateFilter = values[`stateFilter-${criterionId}`];
            const criterionUpdate = acceptanceCriteria.find((c) => c.id === criterionId);

            if (!criterionUpdate) {
              acceptanceCriteria.push({
                id: criterionId,
                displayText,
                description,
                knowledgeBaseArticle,
                stateFilter,
              });
            }
          }

          if (key.startsWith('problem-') || key.startsWith('details-')) {
            const parts = key.split('-');
            const problemId = parts[1];
            const criterionId = parts[3];
            const displayText = values[`problem-${problemId}-criterion-${criterionId}`];
            const details = values[`details-${problemId}-criterion-${criterionId}`];
            const problemUpdate = problems.find((p) => p.id === problemId);
            if (!problemUpdate) {
              problems.push({ id: parts[1], parentId: parts[3], displayText, details });
            }
          }
        }
      });
      updates = {
        ...updates,
        ...(acceptanceCriteria.length > 0 || problems.length > 0
          ? {
              displayTextDiffs: {
                acceptanceCriteria,
                problems,
              },
            }
          : {}),
      };
    }

    if (addNewCriterion && values.displayText) {
      const newProblems: string[] = [];
      Object.keys(values).filter((key) => {
        if (key.startsWith('newProblem-')) {
          newProblems.push(values[key]);
        }
      });
      updates = {
        addItem: {
          itemType: RequirementFlagSettingsItemType.acceptanceCriterion,
          item: {
            displayText: values.displayText,
            description: values.description,
            knowledgeBaseArticle: values.knowledgeBaseArticle,
            ...(newProblems.length > 0
              ? { problems: newProblems.map((problem) => ({ displayText: problem })) }
              : {}),
          },
        },
      };
    }

    if (addNewProblemForCriterion && values.displayText) {
      const newProblems: string[] = [];
      Object.keys(values).filter((key) => {
        if (key.startsWith('newProblem-')) {
          newProblems.push(values[key]);
        }
      });
      updates = {
        addItem: {
          parentId: addNewProblemForCriterion.split('-')[1],
          itemType: RequirementFlagSettingsItemType.problem,
          item: {
            displayText: values.displayText,
            description: values.description,
            knowledgeBaseArticle: values.knowledgeBaseArticle,
            ...(newProblems.length > 0
              ? { problems: newProblems.map((problem) => ({ displayText: problem })) }
              : {}),
          },
        },
      };
    }

    if (values?.knowledgeBaseArticle && knowledgeBaseArticleErrors.length > 0) {
      toast.error(`Knowledge Base Article must be a valid URL for ${knowledgeBaseArticleErrors.join(', ')}`);
      return;
    }

    await handleUpdate(updates);

    if (addNewCriterion || addNewProblemForCriterion) {
      setAddNewCriterion(false);
      setNewProblemForCriterion('');
    }
  };

  const handleSubmitRemoval = async () => {
    if (removingItem) {
      const parts = removingItem.split('-');
      const itemType =
        parts.length === 4
          ? RequirementFlagSettingsItemType.problem
          : RequirementFlagSettingsItemType.acceptanceCriterion;
      const update = { removeItem: { itemType, id: parts[1] } };
      await handleUpdate(update);
      setRemovingItem('');
    }
  };

  return (
    initialValues && (
      <Formik
        validationSchema={flagSettingSchema}
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, resetForm, setFieldValue, submitForm, values }) => {
          const disableEditing = isSubmitting || values.status === RequirementFlagSettingsStatus.inactive;
          return (
            <Form noValidate>
              <Modal
                isOpen={isOpen}
                maxWidth="5xl"
                ariaLabelledBy="editSettingHeader"
                fullScreenMobile
                onDismiss={() => {
                  resetForm();
                  handleClose();
                }}
              >
                <Modal.Header
                  id="editSettingHeader"
                  title={`"${values.name}" Requirement Settings`}
                  onDismiss={() => {
                    resetForm();
                    handleClose();
                  }}
                />
                <Modal.Body background="secondary" childGap="lg">
                  {removingItem ? (
                    <Box as="p">Are you sure you want to remove this item?</Box>
                  ) : addNewCriterion ? (
                    <NewCriterionForm name={values.name} />
                  ) : addNewProblemForCriterion ? (
                    <NewProblemForm name={values.name} parentId={addNewProblemForCriterion} />
                  ) : isEditing ? (
                    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                      <Toggle
                        id="status"
                        label="Active"
                        isChecked={values?.status === RequirementFlagSettingsStatus.active}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            'status',
                            event.target.checked
                              ? RequirementFlagSettingsStatus.active
                              : RequirementFlagSettingsStatus.inactive,
                          );
                        }}
                      />
                      {flagSetting?.type === RequirementFlagSettingsType.incentive && (
                        <Toggle
                          id="isFilterable"
                          label="Is Filterable"
                          isChecked={values?.isFilterable}
                          onChange={() => {
                            setFieldValue('isFilterable', !values?.isFilterable);
                          }}
                          isDisabled={true}
                        />
                      )}
                      {totalCriteriaCount > 0 && (
                        <Box fontWeight="bold">{`Total Acceptance Criteria: ${totalCriteriaCount}`}</Box>
                      )}
                      {criterionProps &&
                        criterionProps?.map(
                          (
                            prop: {
                              criterion: string;
                              description: string;
                              knowledgeBaseArticle: string;
                              stateFilter?: string;
                              canBeRemoved?: boolean;
                            },
                            i: number,
                          ) => {
                            const problems = criteriaProblems[prop.criterion];
                            return (
                              <Card key={i}>
                                <Card.Header childGap="md">
                                  <Box childGap="sm" fontSize="lg" fontWeight="bold" padding="0 0 lg 0">
                                    {`${values.name} Requirement ${i + 1}`}
                                  </Box>
                                  <Field
                                    type="text"
                                    label="Display Text"
                                    name={prop.criterion}
                                    id={prop.criterion}
                                    component={FormikTextareaInput}
                                    isDisabled={disableEditing}
                                  />
                                  <Field
                                    type="text"
                                    label="Description"
                                    name={prop.description}
                                    id={prop.description}
                                    component={FormikTextareaInput}
                                    isDisabled={disableEditing}
                                  />
                                  <Field
                                    type="text"
                                    label="Knowledge Base Article"
                                    name={prop.knowledgeBaseArticle}
                                    id={prop.knowledgeBaseArticle}
                                    component={FormikTextInput}
                                    isDisabled={disableEditing}
                                  />
                                  {values?.isFilterable && (
                                    <Box maxWidth={{ base: '100', desktop: '30' }}>
                                      <Field
                                        type="text"
                                        label="Displays for US state/territory"
                                        name={prop.stateFilter}
                                        id={prop.stateFilter}
                                        options={getStateOptions()}
                                        component={FormikSelectInputNative}
                                      />
                                    </Box>
                                  )}
                                </Card.Header>
                                <Card.Section childGap="sm">
                                  {problems?.length > 0 && (
                                    <Box
                                      borderWidth="0 0 xs 0"
                                      borderColor="grey-100"
                                      padding="0 0 sm 0"
                                      fontWeight="bold"
                                    >{`Total ${values.name} Issues: ${problems.length} `}</Box>
                                  )}
                                  {problems?.map((problem: any, j: number) => {
                                    const problemKey = Object.keys(problem)[0];
                                    const detailsKey = problemKey.replace('problem', 'details');
                                    return (
                                      <Card childGap="sm" key={problemKey}>
                                        <Card.Header childGap="sm">
                                          <Field
                                            type="text"
                                            label={`Issue ${j + 1}`}
                                            name={problemKey}
                                            id={problemKey}
                                            component={FormikTextareaInput}
                                            isDisabled={disableEditing}
                                          />
                                          <Field
                                            type="text"
                                            label="Issue Details (optional; comma separated)"
                                            name={detailsKey}
                                            id={detailsKey}
                                            component={FormikTextareaInput}
                                            isDisabled={disableEditing}
                                          />
                                        </Card.Header>
                                        {problem.canBeRemoved && (
                                          <Card.Footer>
                                            <Button
                                              size="xs"
                                              iconPrefix="remove"
                                              variant="secondary"
                                              tone="danger"
                                              isDisabled={disableEditing}
                                              onClick={() => handleRemoveItem(problemKey)}
                                            >
                                              Remove Issue
                                            </Button>
                                          </Card.Footer>
                                        )}
                                      </Card>
                                    );
                                  })}
                                  <Button
                                    size="sm"
                                    iconPrefix="add"
                                    variant="secondary"
                                    isDisabled={disableEditing}
                                    onClick={() => handleDisplayAddProblem(prop.criterion)}
                                  >
                                    Add Issue
                                  </Button>
                                </Card.Section>
                                {prop.canBeRemoved && (
                                  <Card.Footer>
                                    <Button
                                      size="xs"
                                      variant="primary"
                                      tone="danger"
                                      isDisabled={disableEditing}
                                      onClick={() => handleRemoveItem(prop.criterion)}
                                    >
                                      Remove Requirement
                                    </Button>
                                  </Card.Footer>
                                )}
                              </Card>
                            );
                          },
                        )}

                      <Button
                        size="sm"
                        iconPrefix="add"
                        isDisabled={disableEditing}
                        onClick={handleDisplayAddCriterion}
                      >
                        Add Acceptance Criteria
                      </Button>
                    </Box>
                  ) : (
                    <Box childGap="md">
                      <Box childGap="sm" borderColor="grey-100" borderWidth="0 0 xs 0" padding="0 0 md 0">
                        <Box as="h3">Description</Box>
                        <Box>{flagSetting?.description}</Box>
                      </Box>
                      <Box fontWeight="bold" fontSize="md" padding="md 0 0 0">
                        {`${flagSetting?.acceptanceCriteria && flagSetting.acceptanceCriteria.length > 0 ? 'Available Flags and Issues' : 'No Requirement Criteria Defined'}`}
                      </Box>
                      {flagSetting?.acceptanceCriteria?.map((criterion: RequirementAcceptanceCriterion) => (
                        <Card key={`criterion-list-${criterion?.id?.toString()}`}>
                          <Card.Header fontWeight="bold" childGap="md">
                            <Box>{criterion?.displayText}</Box>
                            <Box fontWeight="medium" fontSize="base">
                              {criterion?.description}
                            </Box>
                          </Card.Header>
                          <Card.Section on padding="0 sm sm sm">
                            <Box as="ul" childGap="sm">
                              {criterion?.problems?.map((problem: RequirementAcceptanceProblem) => (
                                <li key={`problem-list${problem?.id?.toString()}}`}>{problem?.displayText}</li>
                              ))}
                            </Box>
                            <Box padding="md" fontWeight="bold">
                              {criterion?.knowledgeBaseArticle && (
                                <>
                                  <Box>Knowledge Base Article:</Box>

                                  <TextLink href={criterion?.knowledgeBaseArticle} target="_blank">
                                    <Box
                                      fontWeight="medium"
                                      fontSize="base"
                                      padding="md"
                                      direction="row"
                                      childGap="xs"
                                    >
                                      <Box>{criterion.knowledgeBaseArticle}</Box>
                                      <Icon name="launch-app" />
                                    </Box>
                                  </TextLink>
                                </>
                              )}
                            </Box>
                          </Card.Section>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  {isEditing ? (
                    <>
                      <Button
                        variant="secondary"
                        tone="neutral"
                        isLoading={isSubmitting}
                        isDisabled={isSubmitting}
                        onClick={() => {
                          resetForm();
                          if (removingItem || addNewCriterion || addNewProblemForCriterion) {
                            setRemovingItem('');
                            setAddNewCriterion(false);
                            setNewProblemForCriterion('');
                          } else {
                            setIsEditing(false);
                            handleClose();
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        isLoading={isSubmitting}
                        isDisabled={isSubmitting}
                        type="submit"
                        onClick={removingItem ? handleSubmitRemoval : submitForm}
                      >
                        {removingItem ? 'Yes' : 'Save Settings'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      isLoading={isSubmitting}
                      isDisabled={isSubmitting || !flagSetting?.isEditable}
                      type="button"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Modal.Footer>
              </Modal>
            </Form>
          );
        }}
      </Formik>
    )
  );
};
